/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  DatePicker,
  Select,
  TreeSelect,
  notification,
} from 'antd';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';
import * as Sentry from '@sentry/react';
import moment from 'moment';
import {
  costCodeIdTransformer, getLinkedValues, getRelevantAttributeFields, noIdTransformer,
} from 'ontraccr-common/lib/formFields/Attributes';
import { DATATYPES } from 'ontraccr-common/lib/Definitions';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getIdMap, getPopupRef, isNullOrUndefined } from '../../../../helpers/helpers';
import OnTraccrNumberInput from '../../../../common/inputs/OnTraccrNumberInput';
import OnTraccrTextInput from '../../../../common/inputs/OnTraccrTextInput';
// eslint-disable-next-line import/no-cycle
import TimeEntryTableHoursInput from './TimeEntryTableHoursInput';
import TimeEntryTableStartEndTimeInput from './TimeEntryTableStartEndTimeInput';
import OnTraccrSwitch from '../../../../common/inputs/OnTraccrSwitch';

import {
  DROPDOWN_CHANGE_RELATIONS,
  getRelevantDropdownValues,
  luxonToMoment,
  momentToLuxon,
  simpleEquality,
  getDayOffset,
  parseCustomDataFromTemplate,
} from './TimeEntryTable.helpers';
import TimeEntryTableDateTimeInput from './TimeEntryTableDateTimeInput';
import TimeEntryTableDropdownInput from './TimeEntryTableDropdownInput';
import { UNPHASED, convertFromTimeBasedToHourBased, getPhaseOptions } from '../../../../clock/ManualEntry/manualEntryHelpers';
import { getSubContractsWithTotalChangesMap } from '../../../../projects/ProjectScheduleOfValues/helpers';
import { getSubContractDetails } from '../../../state/forms.actions';
import { currencyFormatter, currencyParser } from '../../../../helpers/inputParsers';
import { SHOW_INPUT_TYPES } from './TimeEntryTable.constants';
import OntraccrPopconfirm from '../../../../common/modals/OntraccrPopconfirm';
import { getDropDownOptions } from '../formFieldsHelpers';

import getAttributeConfig from '../attributeConfig';

const HOURS_FIELD_CONFIG = {
  regularHours: {
    startKey: 'startTime',
    endKey: 'endTime',
  },
  otHours: {
    startKey: 'otStartTime',
    endKey: 'otEndTime',
  },
  doubleOTHours: {
    startKey: 'doubleOTStartTime',
    endKey: 'doubleOTEndTime',
  },
};

/**
 *
 * @param {*} props
 * @param {string} props.fieldType - Type of field being rendered
 * @param {string} props.inputType - Type of input to render
 * @param {string} props.title - Title of popover
 * @param {string} props.value - Initial value of input
 * @param {function} props.displayFormatter - Function to format value for display
 * @param {object} props.inputStyle - Style object to apply to input
 * @param {object} props.task - Task object
 * @param {timezone} props.timezone - Task timezone
 * @param {string} props.property - Property of task to update
 * @param {function} props.areEqual - Function to determine if input value is equal to initial value
 * @param {string} props.defaultValue - Default value to use if input is empty
 * @param {array} props.rules - Array of validation rules
 * @param {string} props.width - Width of input
 * @param {boolean} props.readOnly - Whether input is read only
 * @param {function} props.onUpdate - (entryId, payload) => void
 * @param {function} props.getSelectOptions - Accepts all props from  selectOptionProps and task;
 *  outputs an option list for Select
 * @param {object} props.selectOptionProps - Complete list of props for all variants of
 * getSelectOptions
 * @param {object} props.customFieldMap - Map of custom field keys to their properties
 * @returns
 */
function TimeEntryTableInput(props) {
  const {
    fieldType = 'text',
    inputType = 'text',
    title,
    value: initialValue,
    displayFormatter = ({ value }) => value,
    inputStyle,
    task,
    timezone = DateTime.local().zoneName,
    property,
    areEqual,
    getSelectOptions = null,
    selectOptionProps = {},
    customDropdownProps = {},
    defaultCustomData,
    defaultValue,
    rules = [],
    width: inputWidth,
    readOnly = false,
    allowClear = true,
    onUpdate,
    configProps = {},
    responding = false,
    showInput = false,
    customFieldMap = {},
    isSticky = false,
  } = props;

  const now = DateTime.local().toMillis();

  const {
    customData,
    date = TaskHelpers.formatDate(now),
    endTime,
    hourBased,
    id,
    startTime,
    breakEndTime,
    breakStartTime,
    classId,
    costcodeId,
    divisionId,
    doubleOTEndTime,
    doubleOTStartTime,
    ptoEndTime,
    ptoStartTime,
    localId,
    note,
    otEndTime,
    otStartTime,
    phaseId,
    projectId,
    type,
    userId,
    userWage,
    hourlyBillingRate,
    regularBillingRate,
    otBillingRate,
    dtBillingRate,
    hourlyCost,
    dailyCost,
    originalStart,
    originalEnd,
    phasedCostcodeId,
  } = task;

  const {
    projectIdMap = {},
    costcodeIdMap = {},
    userIdMap = {},
    bucketIdMap = {},
    equipmentTypeMap = {},
    equipmentIdMap = {},
    settings = {},
    scheduleOfValues = {},
    subContractMap = {},
    subContractCOMap = {},
    divisions = [],
  } = customDropdownProps;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const equipment = useSelector((state) => state.equipment.equipment);
  const vendors = useSelector((state) => state.vendors.vendors);
  const globalAddressBooks = useSelector((state) => state.contacts.globalAddressBooks);
  const projectEquipment = useSelector((state) => state.equipment.projectEquipment);
  const customers = useSelector((state) => state.customers.customers);

  const divisionSet = useMemo(() => new Set(divisions), [divisions]);
  const selectedUser = useMemo(() => userIdMap[userId], [userId, userIdMap]);
  const attributeMap = useMemo(() => getAttributeConfig(t), [t]);

  const popconfirmRef = useRef(null);
  const containerRef = useRef(null);
  const [showPopover, setShowPopover] = useState(false);
  const [showDisplay, setShowDisplay] = useState(!showInput);
  const [inputValue, setInputValue] = useState(initialValue);
  const [inputOpen, setInputOpen] = useState(false);
  const payablesSubContractMap = useSelector((state) => state.forms.subContractMap);

  const luxonDate = useMemo(() => {
    const parsedDate = TaskHelpers.getTaskDate(task);
    if (!parsedDate?.isValid) {
      return DateTime.local();
    }
    return parsedDate;
  }, [task]);

  const isReadOnly = readOnly;

  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  const switchToDisplayMode = () => {
    if (!showInput) {
      setShowDisplay(true);
      setShowPopover(false);
    }
  };

  const onCancel = () => {
    switchToDisplayMode();
    setInputValue(initialValue);
  };

  // Dynamic Attribute Logic

  const relevantIds = useMemo(() => (
    getRelevantDropdownValues(customData, { multiple: true })
  ), [customData]);

  const subContractsWithTotalChanges = useCallback((dType, linkedProjectIds) => {
    if (dType !== DATATYPES.SubContract) return [];
    return getSubContractsWithTotalChangesMap({
      projectIds: linkedProjectIds,
      subContractMap,
      subContractCOMap,
    });
  }, [subContractMap, subContractCOMap]);

  useEffect(() => {
    if (fieldType !== 'attribute') return;
    const linkedPayableSubContractIds = relevantIds?.payableSubContractIds;
    if (linkedPayableSubContractIds?.length) {
      dispatch(getSubContractDetails(linkedPayableSubContractIds));
    }
  }, [relevantIds, fieldType]);

  const getRelevantDataValues = useCallback((dType, linkedProjectIds, newCustomData) => {
    const newRelevantIds = getRelevantDropdownValues(newCustomData, { multiple: true });
    switch (dType) {
      case DATATYPES.Customer: return [customers, newRelevantIds?.customerIds ?? []];
      case DATATYPES.Contact: return [globalAddressBooks, newRelevantIds?.contactIds ?? []];
      case DATATYPES.ProjectContract: return [scheduleOfValues, []];
      case DATATYPES.Project: return [projectIdMap, newRelevantIds?.projectIds ?? []];
      case DATATYPES.Vendor: return [vendors, newRelevantIds?.vendorIds ?? []];
      case DATATYPES.Costcodes: return [costcodeIdMap, newRelevantIds?.costcodeIds ?? []];
      case DATATYPES.User: return [userIdMap, newRelevantIds?.userIds ?? []];
      case DATATYPES.SubContract: return [
        subContractsWithTotalChanges(dType, linkedProjectIds),
        newRelevantIds?.subContractIds ?? [],
      ];
      case DATATYPES.Buckets: return [bucketIdMap, []];
      case DATATYPES.PayableSubContracts: return [payablesSubContractMap, []];
      default: return [{}, []];
    }
  }, [
    customers,
    globalAddressBooks,
    scheduleOfValues,
    projectIdMap,
    vendors,
    costcodeIdMap,
    userIdMap,
    subContractsWithTotalChanges,
    bucketIdMap,
    payablesSubContractMap,
    relevantIds,
  ]);

  const handleInputParsing = () => {
    let parsedValue = inputValue;
    const parsedCustomData = parseCustomDataFromTemplate(customData ?? {}, defaultCustomData);

    if (fieldType === 'date') {
      if (!moment.isMoment(inputValue) || !inputValue.isValid()) {
        if (configProps.preloadEntryDateType !== 'none') {
          Sentry.withScope(() => {
            Sentry.captureException(new Error('Invalid moment date object provided'));
          });
        }
        parsedValue = luxonDate.toSQLDate();
      } else {
        parsedValue = inputValue.format('YYYY-MM-DD');
      }
    }

    return { parsedValue, parsedCustomData };
  };

  const handlePropertyInteractions = ({ updatedValues, parsedValue }) => {
    const updatedValuesRef = updatedValues;

    if (property !== 'date') {
      updatedValuesRef.date = luxonDate.toSQLDate();
    }

    if (property === 'date') {
      if (!moment.isMoment(inputValue)) {
        Sentry.withScope(() => {
          Sentry.captureException(new Error('Invalid moment date object provided'));
        });
        return;
      }

      const luxDate = momentToLuxon(inputValue);
      const { useEndDateOvernight = false } = settings;

      // Store day offsets for each time pair before updating
      const dayOffsets = useEndDateOvernight
        ? {
          startTime: getDayOffset(endTime, startTime),
          originalStart: getDayOffset(originalEnd, originalStart),
          otStartTime: getDayOffset(otEndTime, otStartTime),
          doubleOTStartTime: getDayOffset(doubleOTEndTime, doubleOTStartTime),
        }
        : {
          endTime: getDayOffset(startTime, endTime),
          originalEnd: getDayOffset(originalStart, originalEnd),
          otEndTime: getDayOffset(otStartTime, otEndTime),
          doubleOTEndTime: getDayOffset(doubleOTStartTime, doubleOTEndTime),
        };

      const millisObj = {
        startTime,
        endTime,
        originalStart,
        originalEnd,
        otStartTime,
        otEndTime,
        doubleOTStartTime,
        doubleOTEndTime,
        breakStartTime,
        breakEndTime,
        ptoStartTime,
        ptoEndTime,
      };

      Object.entries(millisObj).forEach(([key, value]) => {
        if (value) {
          const luxMillis = DateTime.fromMillis(value);
          let baseDate = luxDate;

          // Apply day offset to preserve overnight entries
          if (dayOffsets[key]) {
            baseDate = luxDate.plus({ days: dayOffsets[key] });
          }

          const newMillis = baseDate.set({
            hour: luxMillis.hour,
            minute: luxMillis.minute,
          }).toMillis();
          updatedValuesRef[key] = newMillis;
        }
      });

      updatedValuesRef.dateTimestamp = luxDate.toMillis();
    }

    if (property === 'localId') {
      updatedValuesRef.classId = null;
      updatedValuesRef.class = null;
    }
    if (property === 'userId') {
      updatedValuesRef.regularBillingRate = null;
      updatedValuesRef.otBillingRate = null;
      updatedValuesRef.dtBillingRate = null;
    }
    if (property === 'projectId') {
      updatedValuesRef.costcodeId = null;
      updatedValuesRef.costcode = null;
      updatedValuesRef.dailyCost = null;
      updatedValuesRef.hourlyBillingRate = null;
      updatedValuesRef.regularBillingRate = null;
      updatedValuesRef.otBillingRate = null;
      updatedValuesRef.dtBillingRate = null;
      updatedValuesRef.userWage = null;
      updatedValuesRef.hourlyCost = null;

      const relevantPhases = getPhaseOptions({ ...selectOptionProps, projectId: inputValue });
      if (relevantPhases.length === 1 && relevantPhases[0].value === UNPHASED) {
        updatedValuesRef.phaseId = UNPHASED;
        updatedValuesRef.phase = UNPHASED;
      } else {
        updatedValuesRef.phaseId = null;
      }
    }
    if (property === 'divisionId') {
      const { divisionMap = {} } = selectOptionProps;
      updatedValuesRef.projectId = null;
      updatedValuesRef.project = null;
      updatedValuesRef.phaseId = null;
      updatedValuesRef.phase = null;
      updatedValuesRef.costcodeId = null;
      updatedValuesRef.costcode = null;
      updatedValuesRef.dailyCost = null;
      updatedValuesRef.hourlyBillingRate = null;
      updatedValuesRef.regularBillingRate = null;
      updatedValuesRef.otBillingRate = null;
      updatedValuesRef.dtBillingRate = null;
      updatedValuesRef.userWage = null;
      updatedValuesRef.hourlyCost = null;
      updatedValuesRef.division = divisionMap[parsedValue]?.name ?? null;
    }
    if (property === 'phaseId') {
      updatedValuesRef.costcodeId = null;
      updatedValuesRef.costcode = null;
    }
    if (property === 'costcodeId') {
      updatedValuesRef.hourlyBillingRate = null;
      updatedValuesRef.regularBillingRate = null;
      updatedValuesRef.otBillingRate = null;
      updatedValuesRef.dtBillingRate = null;
      updatedValuesRef.dailyCost = null;
      updatedValuesRef.hourlyCost = null;
      updatedValuesRef.userWage = null;
    }
    if (property === 'hourBased' && updatedValuesRef.hourBased) {
      const newTimes = convertFromTimeBasedToHourBased({
        startTime,
        endTime,
        originalStart,
        originalEnd,
        otStartTime,
        otEndTime,
        doubleOTStartTime,
        doubleOTEndTime,
        breakStartTime,
        breakEndTime,
        ptoStartTime,
        ptoEndTime,
        date,
        timezone,
      });
      Object.keys(newTimes).forEach((key) => {
        updatedValuesRef[key] = newTimes[key];
      });
    }

    if (property === 'excludeFromAutoBreak') {
      updatedValuesRef.excludeFromAutoBreak = parsedValue;
    }

    if (property === 'isNightShift') {
      updatedValuesRef.isNightShift = parsedValue;
    }

    if (property === 'projectId' || fieldType === 'dropdown') {
      // handle clearing any relevant dropdown values
      let dropdownsToCheck = DROPDOWN_CHANGE_RELATIONS.Projects;

      if (property !== 'projectId') {
        const {
          configProps: {
            dataType,
          } = {},
        } = customFieldMap[property] ?? {};
        dropdownsToCheck = DROPDOWN_CHANGE_RELATIONS[dataType];
      }

      if (dropdownsToCheck) {
        const newCustomData = updatedValuesRef.customData;
        Object.keys(newCustomData ?? {})?.forEach((key) => {
          if (!key?.startsWith('field-')) return;

          const field = newCustomData?.[key];

          if (!field) return;

          const {
            dataType,
            values = [],
          } = field ?? {};

          if (!dropdownsToCheck.has(dataType)) return;

          const fullField = customFieldMap[key];

          const { data: newOptions = [] } = getDropDownOptions({
            actualSelectedSet: new Set(values ?? []),
            configProps: fullField?.configProps ?? {},
            divisionSet: new Set([divisionId]),
            responding,
            responses: newCustomData,
            ...customDropdownProps,
            ...getRelevantDropdownValues(newCustomData),
            projectId: updatedValuesRef.projectId ?? customDropdownProps?.projectId,
          });

          const newValueMap = getIdMap(newOptions);

          updatedValuesRef.customData[key] = {
            ...field,
            values: values?.filter(({ id: vId } = {}) => !!newValueMap?.[vId]),
          };
        });
      }
    }

    if (fieldType === 'dropdown') {
      // Handle dynamic attribute linked value changes
      const relevantAttributeFields = getRelevantAttributeFields(
        property,
        customData,
        configProps?.dataType,
      );
      if (!relevantAttributeFields) return;

      const linkedIds = parsedValue.filter((val) => !!val).map(({ id: lId }) => lId);
      const lastIds = customData?.[property]?.values?.map(({ id: lId }) => lId) ?? [];

      relevantAttributeFields.forEach((fieldId) => {
        const field = updatedValuesRef.customData[fieldId];
        if (!field) return;

        const {
          value: attributeValue,
          dataType: attributeDataType,
          attribute,
          type: initialFieldType = '',
          useFormAuthorAttributes,
          linkField,
        } = field;

        /*
          In TimeEntryTablePreview we set defaultCustomData to be responses
          from prepareResponsePayload.

          prepareResponsePayload sets type of the field
          to the field type (e.g. 'attribute')

          we need to pull the real type from thee ATTRIBUTE_CONFIG
        */

        const ourAttribute = attributeMap[attributeDataType]?.fields
          ?.find((attField) => attField?.attr === attribute);

        const [data, dataDefaultValue] = getRelevantDataValues(
          attributeDataType,
          linkedIds,
          updatedValuesRef.customData,
        );

        const linkedValues = getLinkedValues({
          dataType: attributeDataType,
          attribute,
          type: ourAttribute?.type ?? initialFieldType,
          useFormAuthorAttributes,
          linkField,
          responses: updatedValuesRef.customData,
          lastIds,
          currentValue: attributeValue,
          data,
          defaultValue: dataDefaultValue,
          idTransformer: attributeDataType === DATATYPES.Costcodes
            ? costCodeIdTransformer
            : noIdTransformer,
          authorId: selectedUser?.id,
          extraProps: {
            equipment,
            projectEquipment,
            equipmentTypeMap,
            divSet: divisionSet,
          },
        });

        if (!linkedValues) return;

        const { newValue: newAttributeValue } = linkedValues;

        updatedValuesRef.customData[fieldId] = {
          ...field,
          value: newAttributeValue,
        };
      });
    }
  };

  const handlePropertyValueChange = ({ updatedValues, parsedCustomData, parsedValue }) => {
    const updatedValuesRef = updatedValues;
    switch (fieldType) {
      case 'text':
      case 'attribute':
      case 'yes-no': {
        updatedValuesRef.customData = {
          ...parsedCustomData,
          [property]: {
            ...(defaultCustomData?.[property] ?? {}),
            ...(parsedCustomData[property] || {}),
            value: parsedValue,
          },
        };
        break;
      }
      case 'dateTime': {
        updatedValuesRef.customData = {
          ...parsedCustomData,
          [property]: {
            ...(defaultCustomData?.[property] ?? {}),
            ...(parsedCustomData[property] || {}),
            ...parsedValue,
          },
        };
        break;
      }
      case 'dropdown': {
        updatedValuesRef.customData = {
          ...parsedCustomData,
          [property]: {
            ...(defaultCustomData?.[property] ?? {}),
            ...(parsedCustomData[property] || {}),
            values: parsedValue?.filter((val) => !!val) ?? [],
          },
        };
        break;
      }
      case 'formattedStartTime':
      case 'formattedEndTime':
      case 'time': {
        property.forEach((key) => {
          updatedValuesRef[key] = isNullOrUndefined(parsedValue[key]) && defaultValue?.[key]
            ? defaultValue[key] : parsedValue[key];
        });
        break;
      }
      case 'regularHours':
      case 'otHours':
      case 'doubleOTHours': {
        const { startKey, endKey } = HOURS_FIELD_CONFIG[fieldType];

        const hoursNum = Number.parseFloat(parsedValue);
        if (isNullOrUndefined(parsedValue) || Number.isNaN(hoursNum)) {
          updatedValuesRef[startKey] = null;
          updatedValuesRef[endKey] = null;
          break;
        }
        const hours = Math.min(24, Math.max(0, hoursNum));
        const totalMs = hours * 60 * 60 * 1000;
        const dayStart = luxonDate.startOf('day').toMillis();
        updatedValuesRef[startKey] = dayStart;
        updatedValuesRef[endKey] = dayStart + totalMs;
        break;
      }
      case 'project': {
        updatedValuesRef.customData = {
          ...parsedCustomData,
        };
        updatedValuesRef.projectId = isNullOrUndefined(inputValue) && defaultValue
          ? defaultValue : parsedValue;
        break;
      }
      default: {
        updatedValuesRef[property] = isNullOrUndefined(inputValue) && defaultValue
          ? defaultValue : parsedValue;
        break;
      }
    }
  };

  const onConfirm = async () => {
    // Early return if value hasn't changed - prevents unnecessary updates
    // when user clicks away without modifying the input
    if (areEqual(inputValue, initialValue)) {
      switchToDisplayMode();
      return;
    }

    if (rules && rules.length > 0) {
      const errors = rules.filter((rule) => !rule.validator(inputValue));
      if (errors.length > 0) {
        notification.error({
          message: 'Error',
          description: errors[0].message,
        });
        return;
      }
    }
    const { parsedValue, parsedCustomData } = handleInputParsing();

    const updatedValues = {};

    handlePropertyValueChange({ updatedValues, parsedValue, parsedCustomData });
    handlePropertyInteractions({ updatedValues, parsedValue });

    const payload = {
      breakEndTime,
      breakStartTime,
      canEdit: !readOnly,
      classId,
      costcodeId: phasedCostcodeId ?? costcodeId,
      customData: parsedCustomData,
      date,
      divisionId,
      doubleOTEndTime,
      doubleOTStartTime,
      ptoStartTime,
      ptoEndTime,
      endTime,
      hourBased,
      localId,
      note,
      otEndTime,
      otStartTime,
      phaseId,
      projectId,
      startTime,
      timezone,
      type,
      userWage,
      hourlyBillingRate,
      regularBillingRate,
      otBillingRate,
      dtBillingRate,
      dailyCost,
      hourlyCost,
      originalStart,
      originalEnd,
      ...updatedValues,
    };

    onUpdate(id, payload, property);
    switchToDisplayMode();
  };

  const onChange = (e) => {
    if (isReadOnly) return; // Prevent changes when read-only

    if (inputType === 'text' && !configProps?.isNumerical) {
      setInputValue(e.target.value);
    } else {
      setInputValue(e);
    }
    setInputOpen(false);
    if (inputType === 'switch') {
      // For some reason calling onConfirm here doesn't work
      const parsedCustomData = parseCustomDataFromTemplate(customData ?? {}, defaultCustomData);

      const payload = {
        breakEndTime,
        breakStartTime,
        canEdit: !readOnly,
        classId,
        costcodeId,
        customData: parsedCustomData,
        date,
        divisionId,
        doubleOTEndTime,
        doubleOTStartTime,
        ptoStartTime,
        ptoEndTime,
        endTime,
        hourBased,
        localId,
        note,
        otEndTime,
        otStartTime,
        phaseId,
        projectId,
        startTime,
        timezone,
        type,
        userWage,
        hourlyBillingRate,
        regularBillingRate,
        otBillingRate,
        dtBillingRate,
        dailyCost,
        hourlyCost,
        originalStart,
        originalEnd,
        [property]: e,
      };
      onUpdate(id, payload, property);
    }
  };

  const InputComponent = useCallback((inputProps) => {
    switch (inputType) {
      case 'number': {
        return <OnTraccrNumberInput {...inputProps} />;
      }
      case 'hours': {
        if (!hourBased) {
          return null;
        }

        const { minuteIncrement = 1 } = settings;
        return (
          <OnTraccrNumberInput
            {...inputProps}
            min={0}
            max={24}
            step={minuteIncrement / 60}
            precision={2}
          />
        );
      }
      case 'currency': {
        return (
          <OnTraccrNumberInput
            {...inputProps}
            formatter={currencyFormatter}
            parser={currencyParser}
          />
        );
      }
      case 'dateTime': {
        return (
          <TimeEntryTableDateTimeInput
            {...inputProps}
            configProps={configProps}
            timezone={timezone}
          />
        );
      }
      case 'date': {
        const { value: dateValue, ...rest } = inputProps;
        const defaultDateValue = dateValue?.isValid() ? dateValue : luxonToMoment(DateTime.local().startOf('day'));
        return <DatePicker {...rest} value={defaultDateValue} allowClear={false} />;
      }
      case 'select': {
        if (fieldType === 'dropdown') {
          return (
            <TimeEntryTableDropdownInput
              {...inputProps}
              customDropdownProps={customDropdownProps}
              configProps={configProps}
              task={task}
              customData={customData}
              value={customData?.[property]?.values}
              responding={responding}
              responses={task.customData ?? {}}
            />
          );
        }

        const { enableGlobalCCOnProject = false } = settings;

        const selectOptions = getSelectOptions({
          ...task,
          ...selectOptionProps,
          settings,
        });

        return (
          enableGlobalCCOnProject && task?.projectId ? (
            <TreeSelect
              {...inputProps}
              allowClear={allowClear}
              showSearch
              treeDefaultExpandAll
              treeNodeFilterProp="title"
              treeData={selectOptions}
            />
          ) : (
            <Select
              {...inputProps}
              allowClear={allowClear}
              options={selectOptions}
            />
          )
        );
      }
      case 'time': {
        return (
          <TimeEntryTableHoursInput
            {...inputProps}
            date={luxonDate}
            timezone={timezone}
            hourBased={!!hourBased}
          />
        );
      }
      case 'startEndTime': {
        const modifier = fieldType === 'formattedStartTime' ? 'start' : 'end';
        const {
          autoFocus,
          ...rest
        } = inputProps;
        return (
          <TimeEntryTableStartEndTimeInput
            {...rest}
            date={luxonDate}
            hourBased={!!hourBased}
            timezone={timezone}
            type={type}
            modifier={modifier}
          />
        );
      }
      case 'switch': {
        return (
          <div style={{ flexDirection: 'column', justifyContent: 'center' }}>
            <OnTraccrSwitch {...inputProps} disabled={isReadOnly} />
          </div>
        );
      }
      default: {
        const {
          isNumerical,
          isCharacterLimit,
          characterLimit,
        } = configProps;

        const {
          value,
        } = inputProps;

        if (isNumerical) {
          return (
            <OnTraccrNumberInput
              {...inputProps}
            />
          );
        }

        return (
          <div
            style={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <OnTraccrTextInput
              {...inputProps}
              maxLength={isCharacterLimit ? (characterLimit || 1) : undefined}
              style={isCharacterLimit ? { paddingRight: '70px' } : {}}
            />
            {isCharacterLimit && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '5px',
                  right: '10px',
                  fontSize: '12px',
                }}
              >
                {value ? value.length : 0} / {characterLimit || 1}
              </div>
            )}
          </div>
        );
      }
    }
  }, [inputType, hourBased, selectOptionProps, task, fieldType, property, isReadOnly]);

  const onDisplayClick = () => {
    if (!isReadOnly) {
      setShowDisplay(false);
      setInputOpen(true);
    }
  };

  const onKeyDown = (e) => {
    if (!showDisplay) {
      e.stopPropagation();
    }

    // Close popover on escape
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  const onBlur = (e) => {
    if (!inputOpen) return;

    if (containerRef.current && containerRef.current.contains(e.relatedTarget)) {
      return;
    }

    const popupRef = getPopupRef(popconfirmRef);
    if (popupRef && popupRef.current && popupRef.current.contains(e.relatedTarget)) {
      return;
    }

    const notificationContainer = document.querySelector('.ant-notification');
    if (notificationContainer && notificationContainer.contains(e.relatedTarget)) {
      return;
    }

    onCancel();
  };

  const onFocus = () => {
    setInputOpen(true);
  };

  useEffect(() => {
    if (!showDisplay) {
      const isInputDirty = !areEqual(inputValue, initialValue);
      setShowPopover(isInputDirty);
    }
  }, [showDisplay, inputValue, initialValue, areEqual]);

  const displayValue = useMemo(() => {
    const display = displayFormatter({
      value: inputValue,
      task,
      property,
      timezone,
      projectIdMap,
      equipmentIdMap,
      settings,
    });

    return (isNullOrUndefined(display) || display === '') ? <> &nbsp; </> : display;
  }, [
    fieldType,
    displayFormatter,
    inputValue,
    task,
    property,
    timezone,
    equipmentIdMap,
    projectIdMap,
    settings,
  ]);

  const cellItem = useMemo(() => {
    if (!showDisplay) {
      return (
        <InputComponent
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onFocus}
          open={inputOpen}
          onPressEnter={onConfirm}
          autoFocus
          value={inputValue}
          style={{
            borderRadius: 6,
            borderColor: '#323232',
            width: '100%',
            marginRight: SHOW_INPUT_TYPES.has(inputType) ? 10 : 0,
            ...inputStyle,
          }}
          suffixIcon={null}
          onConfirm={onConfirm}
        />
      );
    }

    return (
      <div
        onClick={onDisplayClick}
        style={{
          background: 'none',
          color: 'inherit',
          border: 'none',
          padding: 0,
          font: 'inherit',
          cursor: readOnly ? 'auto' : 'pointer',
          outline: 'inherit',
          display: 'table-cell',
          verticalAlign: 'middle',
          textAlign: 'left',
          maxWidth: inputWidth || 250,
          minWidth: inputWidth || 250,
          textWrap: 'wrap',
        }}
      >
        {displayValue}
      </div>
    );
  }, [inputOpen, inputValue, onChange, onBlur, onFocus, onConfirm, displayValue, inputWidth]);

  let containerStyle = {};
  if (!showDisplay) {
    if (isSticky) {
      containerStyle = {
        maxWidth: inputWidth || 250,
        minWidth: inputWidth || 250,
        zIndex: 1,
      };
    } else {
      containerStyle = {
        position: 'absolute',
        transform: 'translate(-10px, -15px)',
        maxWidth: inputWidth || 250,
        minWidth: inputWidth || 250,
        marginLeft: inputWidth ? 10 : 0,
        zIndex: 1,
      };
    }
  }

  return (
    <div
      ref={containerRef}
      onKeyDown={onKeyDown}
      style={containerStyle}
      role="button"
      tabIndex={0}
    >
      {SHOW_INPUT_TYPES.has(inputType)
        ? cellItem
        : (
          <OntraccrPopconfirm
            visible={showPopover}
            title={title}
            okText="Update"
            onConfirm={onConfirm}
            onCancel={onCancel}
            ref={popconfirmRef}
            placement="left"
          >
            {cellItem}
          </OntraccrPopconfirm>
        )}

    </div>
  );
}

const MemoizedTimeEntryTableInputComponent = React.memo(TimeEntryTableInput);

export default MemoizedTimeEntryTableInputComponent;

TimeEntryTableInput.propTypes = {
  fieldType: PropTypes.string,
  inputType: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.any,
  ]),
  displayFormatter: PropTypes.func,
  inputStyle: PropTypes.shape({}),
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    costcodeId: PropTypes.string,
    phaseId: PropTypes.string,
    projectId: PropTypes.string,
    divisionId: PropTypes.string,
    date: PropTypes.string,
    note: PropTypes.string,
    type: PropTypes.string,
    startTime: PropTypes.number,
    endTime: PropTypes.number,
    hourBased: PropTypes.bool,
    customData: PropTypes.oneOf(
      [PropTypes.shape({})],
      [PropTypes.arrayOf(PropTypes.shape({}))],
    ),
  }).isRequired,
  getSelectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  selectOptionProps: PropTypes.shape({
    activeCostcodes: PropTypes.arrayOf(PropTypes.shape({})),
    activeProjects: PropTypes.arrayOf(PropTypes.shape({})),
    activePhases: PropTypes.arrayOf(PropTypes.shape({})),
    user: PropTypes.shape({}),
    users: PropTypes.arrayOf(PropTypes.shape({})),
    teams: PropTypes.arrayOf(PropTypes.shape({})),
    userDivisions: PropTypes.arrayOf(PropTypes.shape({})),
    divisionId: PropTypes.string,
    projectId: PropTypes.string,
    phaseId: PropTypes.string,
  }),
  areEqual: PropTypes.func,
  property: PropTypes.string,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.any,
  ]),
  rules: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string,
    validator: PropTypes.func,
  })),
  width: PropTypes.number,
  readOnly: PropTypes.bool,
  allowClear: PropTypes.bool,
  customDropdownProps: PropTypes.shape({}),
  timezone: PropTypes.string,
  configProps: PropTypes.shape({}),
  responding: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
  isSticky: PropTypes.bool,
};

TimeEntryTableInput.defaultProps = {
  fieldType: 'text',
  inputType: 'text',
  title: '',
  value: '',
  displayFormatter: null,
  inputStyle: {},
  areEqual: simpleEquality,
  defaultValue: null,
  timezone: DateTime.local().zoneName,
  rules: [],
  width: null,
  readOnly: false,
  allowClear: true,
  property: null,
  getSelectOptions: null,
  selectOptionProps: {},
  customDropdownProps: {},
  configProps: {},
  responding: false,
  isSticky: false,
};
